import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Modal } from '@shape-ds/components';
import { AxiosError } from 'axios';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PlotView } from 'queries/scalings/contract';
import { dayjs, Dayjs } from 'lib';
import { InfoMessage } from '@shape/lighthouse-components';
import { getCssVar } from 'utils';
import { ExpandIcon } from 'components/ExpandIcon/ExpandIcon';
import usePlotScaling from './usePlotScaling';

import './style.scss';
import PlotScalingLegend from './PlotScalingLegend';
import { defaultGraphicOptions } from './graphicOptions';

export const PlotScaling = ({
  data,
  error,
  isLoading,
  period,
  modelName,
  modelType,
  modelDate,
  hasTitle = true,
  hasExpandIcon = true,
}: {
  data?: PlotView;
  error?: AxiosError<unknown, any> | null;
  isLoading?: boolean;
  period: [Dayjs | null, Dayjs | null] | null;
  modelName: string;
  modelType: string;
  modelDate: string;
  hasTitle?: boolean;
  hasExpandIcon?: boolean;
}) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { filters, dotsSeries, learnedRegion, operationLines, showObservationMessage } =
    usePlotScaling({ data, error, isLoading, period, modelName, modelType, modelDate });
  const series = useMemo(
    () => ({
      xAxis: [
        {
          type: 'value',
          name: data?.scaling_data?.unit?.x,
          scale: true,
          axisLabel: {
            formatter: '{value}',
            color: '#999999',
          },
          axisLine: {
            onZero: false,
          },
          splitLine: {
            lineStyle: {
              color: '#444444',
            },
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: data?.scaling_data?.unit?.y,
          scale: true,
          axisLabel: {
            formatter: '{value}',
            color: '#999999',
          },
          axisLine: {
            onZero: false,
          },
          splitLine: {
            lineStyle: {
              color: '#444444',
            },
          },
        },
      ],
      series: [...dotsSeries(), ...learnedRegion(), ...operationLines()],
    }),
    [dotsSeries, learnedRegion, operationLines],
  );

  if (error || !data) {
    return error?.response?.status === 404 ? (
      <div className="scaling-error-container">
        <InfoMessage
          type="warning"
          title={t('messages:model_not_found.title')}
          message={t('messages:model_not_found.message')}
        />
      </div>
    ) : (
      <div className="scaling-error-container">
        <InfoMessage
          type="error"
          title={t('messages:error.title')}
          message={t('messages:error.message')}
        />
      </div>
    );
  }

  if (
    !data.scaling_data.response_data.curve_points &&
    data.scaling_data.response_data.grid_points?.length === 0
  ) {
    return (
      <div className="scaling-error-container">
        <InfoMessage
          type="warning"
          title={t('messages:no_data.title')}
          message={t('messages:no_data.message')}
        />
      </div>
    );
  }

  const renderGraph = (isInsideModal?: boolean) => (
    <>
      {hasTitle && (
        <h1 className="is-size-5 has-text-weight-bold p-0 pl-2">
          {t('components:plot_scaling.title')}
        </h1>
      )}
      <div className="observations is-flex">
        <div className="observation-message">
          {t('components:plot_scaling.message.model_version')}:{' '}
          {dayjs(modelDate).format('DD/MM/YYYY')}
        </div>
        {showObservationMessage && (
          <div className="observation-message observation-message__warning">
            <FontAwesomeIcon
              className="py-0"
              icon={faInfoCircle}
              color={getCssVar('--red-600')}
              style={{ fontSize: 12 }}
            />{' '}
            {t('components:plot_scaling.message.selected_period_is_older')}
          </div>
        )}
      </div>
      <div className="columns mb-0">
        <div
          className="column pb-0"
          style={{
            marginLeft: '-10px',
            width: '100%',
          }}
        >
          <ReactECharts
            echarts={echarts}
            style={{ height: isInsideModal ? '550px' : '420px', width: '100%' }}
            option={{ ...defaultGraphicOptions, ...series }}
          />
        </div>
      </div>
      <div>
        <PlotScalingLegend modelType={modelType} filters={filters} />
      </div>
    </>
  );
  return (
    <>
      {hasExpandIcon && <ExpandIcon onClick={() => setIsModalVisible(true)} />}
      <div className="plotScalingContainer">{renderGraph()}</div>
      <Modal open={isModalVisible} onClose={() => setIsModalVisible(false)}>
        {renderGraph(true)}
      </Modal>
    </>
  );
};
